import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface IndustryContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', IndustryContent> = {
  en: {
    metaTitle: 'Identity & access for healthcare',
    metaDescription:
      'Tap-and-go clinician access, PHI audit, and HIPAA / KVKK / GDPR-ready governance for hospitals, payers, and clinical-data platforms.',
    heroEyebrow: 'Industries · Healthcare',
    heroTitle: ['Identity at the', 'point of care.'],
    heroBody:
      'Tap-and-go for clinicians, brokered access for EMR admins, and an audit trail that holds up under HIPAA, KVKK, and GDPR review. Identity that helps the workflow, not the breach.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'HIPAA / KVKK / GDPR-aligned',
      'Tap-and-go clinician sign-in',
      'PHI access auditing',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What healthcare teams get with Monofor.',
    pillarsBody:
      'Healthcare identity has to clear two bars: clinicians need to work fast, and compliance has to be unambiguous. Monofor was built to clear both.',
    pillars: [
      {
        title: 'Fast access at the point of care',
        description:
          'Clinicians are not going to fight an MFA prompt during a code blue. Monosign supports tap-and-go, badge sign-in, and roaming sessions across shared workstations.',
        bullets: [
          'Tap-and-go with proximity cards',
          'Shared-workstation roaming sessions',
          'Single sign-on across EMR, PACS, and lab systems',
          'Configurable session-timeout per ward',
        ],
      },
      {
        title: 'PHI access, on the record',
        description:
          'Every access to patient data is logged at the identity layer. Monitor break-glass usage, anomalous chart access, and after-hours activity.',
        bullets: [
          'Per-record access audit trail',
          'Break-glass policy with manager notification',
          'Anomaly detection on chart-access patterns',
          'Retention configurable by jurisdiction',
        ],
      },
      {
        title: 'Privileged access for the data center',
        description:
          'EMR administrators, biomedical engineers, and database operators get brokered, recorded, time-bound access, not domain admin keys.',
        bullets: [
          'Brokered access to Epic, Cerner, and lab systems',
          'Session recording with PHI redaction',
          'Just-in-time elevation tied to change tickets',
          'Vendor / contractor access with hard expiry',
        ],
      },
      {
        title: 'HIPAA / KVKK / GDPR-ready',
        description:
          'Pre-built control mappings for healthcare frameworks. Configurable data-residency for cross-border ops. Reports auditors can hand to inspectors.',
        bullets: [
          'HIPAA Security Rule control mapping',
          'KVKK and GDPR for European / Turkish ops',
          'Data residency in EU, Turkey, or self-hosted',
          'Signed, exportable evidence packets',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From bedside to back office to inspector.',
    steps: [
      {
        title: 'Federate clinical apps',
        description:
          'Bring EMR, PACS, lab systems, and pharmacy systems under one sign-on. Add MFA where the workflow tolerates it.',
      },
      {
        title: 'Broker privileged access',
        description:
          'Route every EMR admin, biomed engineer, and DBA session through Monopam. Record. Time-bound. Audit.',
      },
      {
        title: 'Watch and prove',
        description:
          'Monitor PHI access patterns in real time. Export audit packets for regulators on demand.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Audit & compliance',
        description: 'Use case: PHI access governance and audit readiness.',
        href: '/solutions/audit-compliance',
      },
      {
        label: 'Privileged session recording',
        description: 'Evidence backbone for clinical system access.',
        href: '/pam/session-recording',
      },
      {
        label: 'Identity governance',
        description: 'Entitlement mapping across clinical and back-office systems.',
        href: '/sync/governance',
      },
    ],
  },
  tr: {
    metaTitle: 'Sağlık sektörü için kimlik ve erişim',
    metaDescription:
      'Hastaneler, ödeyici kurumlar ve klinik veri platformları için dokun-geç klinisyen erişimi, PHI denetimi ve HIPAA / KVKK / GDPR’a hazır yönetişim.',
    heroEyebrow: 'Sektörler · Sağlık',
    heroTitle: ['Bakım noktasında', 'kimlik.'],
    heroBody:
      'Klinisyenler için dokun-geç erişim, EMR yöneticileri için aracılı erişim ve HIPAA, KVKK ve GDPR incelemesinde ayakta kalan bir denetim izi. İhlale değil, iş akışına yardım eden kimlik.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'HIPAA / KVKK / GDPR ile uyumlu',
      'Dokun-geç klinisyen girişi',
      'PHI erişim denetimi',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Sağlık ekiplerinin Monofor ile elde ettikleri.',
    pillarsBody:
      'Sağlıkta kimlik iki çıtayı birden aşmak zorunda: klinisyenler hızlı çalışabilmeli, uyum ise tartışmasız olmalı. Monofor ikisini de aşmak için tasarlandı.',
    pillars: [
      {
        title: 'Bakım noktasında hızlı erişim',
        description:
          'Klinisyenler mavi kod sırasında MFA istemiyle boğuşmaz. Monosign; dokun-geç girişi, kartla oturum açmayı ve ortak iş istasyonları arasında gezici oturumları destekler.',
        bullets: [
          'Yakınlık kartlarıyla dokun-geç giriş',
          'Ortak iş istasyonlarında gezici oturumlar',
          'EMR, PACS ve laboratuvar sistemlerinde tek oturum açma',
          'Servis başına yapılandırılabilir oturum zaman aşımı',
        ],
      },
      {
        title: 'PHI erişimi, kayıt altında',
        description:
          'Hasta verisine her erişim kimlik katmanında loglanır. Acil erişim (break-glass) kullanımını, anormal dosya erişimini ve mesai dışı etkinliği izleyin.',
        bullets: [
          'Kayıt bazında erişim denetim izi',
          'Yönetici bildirimli acil erişim (break-glass) politikası',
          'Dosya erişim desenlerinde anomali tespiti',
          'Yargı bölgesine göre yapılandırılabilir saklama süresi',
        ],
      },
      {
        title: 'Veri merkezi için ayrıcalıklı erişim',
        description:
          'EMR yöneticileri, biyomedikal mühendisler ve veritabanı operatörleri; domain admin anahtarları yerine aracılı, kayıtlı ve süreli erişim alır.',
        bullets: [
          'Epic, Cerner ve laboratuvar sistemlerine aracılı erişim',
          'PHI maskeleme ile oturum kaydı',
          'Değişiklik kayıtlarına bağlı tam zamanında yetki yükseltme',
          'Kesin süre sonlu tedarikçi / yüklenici erişimi',
        ],
      },
      {
        title: 'HIPAA / KVKK / GDPR’a hazır',
        description:
          'Sağlık çerçeveleri için hazır kontrol eşlemeleri. Sınır ötesi operasyonlar için yapılandırılabilir veri yerleşimi. Denetçilerin müfettişlere sunabileceği raporlar.',
        bullets: [
          'HIPAA Güvenlik Kuralı kontrol eşlemesi',
          'Avrupa / Türkiye operasyonları için KVKK ve GDPR',
          'AB’de, Türkiye’de veya kendi altyapınızda veri yerleşimi',
          'İmzalı, dışa aktarılabilir kanıt paketleri',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Hasta başından arka ofise, oradan müfettişe.',
    steps: [
      {
        title: 'Klinik uygulamaları federe edin',
        description:
          'EMR, PACS, laboratuvar ve eczane sistemlerini tek oturum açma altında toplayın. İş akışının izin verdiği yerlere MFA ekleyin.',
      },
      {
        title: 'Ayrıcalıklı erişimi aracılayın',
        description:
          'Her EMR yöneticisi, biyomedikal mühendis ve DBA oturumunu Monopam üzerinden yönlendirin. Kaydedin. Süreyle sınırlayın. Denetleyin.',
      },
      {
        title: 'İzleyin ve kanıtlayın',
        description:
          'PHI erişim desenlerini gerçek zamanlı izleyin. Denetim paketlerini regülatörler için istendiği anda dışa aktarın.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Denetim ve uyum',
        description: 'Kullanım senaryosu: PHI erişim yönetişimi ve denetim hazırlığı.',
        href: '/solutions/audit-compliance',
      },
      {
        label: 'Ayrıcalıklı oturum kaydı',
        description: 'Klinik sistem erişimi için kanıt omurgası.',
        href: '/pam/session-recording',
      },
      {
        label: 'Kimlik yönetişimi',
        description: 'Klinik ve arka ofis sistemlerinde yetki eşleme.',
        href: '/sync/governance',
      },
    ],
  },
}

type Props = { locale: string }

export default function HealthcarePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/industries/healthcare"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
